import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertCircle, CheckCircle, Crown, Calendar, Users, FileText, FolderOpen, AlertTriangle, Settings } from "lucide-react";
import { useState } from "react";
import { SeatUpgradePrompt } from "@/components/SeatUpgradePrompt";
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '@/utils/date-formatter';
import { subscriptionPlanNameToSlug } from '@/utils/subscription-plan-slug';

type DisplayFeature = { key: string; label: string };

type SubPlan = {
  id: string;
  plan_name?: string;
  planName?: string;
  price_per_seat_monthly?: number;
  price_per_seat_yearly?: number;
  pricePerSeatMonthly?: number;
  pricePerSeatYearly?: number;
  included_seats?: number;
  min_seats?: number;
  max_seats?: number;
  maxProjectsPerSeat?: number | null;
  max_projects_per_seat?: number | null;
  maxPdfPlansPerSeat?: number | null;
  max_pdf_plans_per_seat?: number | null;
  maxProjects?: number | null;
  max_projects?: number | null;
  maxPdfPlans?: number | null;
  max_pdf_plans?: number | null;
  features?: string[];
  displayFeatures?: DisplayFeature[];
  currency?: string;
};

type SubscriptionTabProps = {
  subscriptionPlan: any; // Organization subscription data
  subscriptionUsage: any; // Subscription limits data  
  subscription: any; // Organization subscription data (same as subscriptionPlan)
  invoices: any[];
  availablePlans: SubPlan[];
  selectedProUsers: number;
  setSelectedProUsers: (n: number) => void;
  selectedRiseUsers: number;
  setSelectedRiseUsers: (n: number) => void;
  billingPeriods: Record<string, 'monthly' | 'yearly'>;
  setBillingPeriods: (updater: (prev: Record<string, 'monthly' | 'yearly'>) => Record<string, 'monthly' | 'yearly'>) => void;
  calculateProPrice: (users: number, isYearly?: boolean) => number;
  calculateRisePrice: (users: number, isYearly?: boolean) => number;
  formatPrice: (price: number) => string | number;
  isDowngrade: (targetPlan: string, targetUsers: number, targetBilling: string, currentPlan: string, currentUsers?: number, currentBilling?: string) => boolean;
  formatDate: (dateString: string) => string;
  getStatusBadge: (status: string) => JSX.Element;
  upgradeMutation: { isPending: boolean; mutate: (payload: any) => void };
  setUpgradeDialog: (dlg: any) => void;
  hasPermission: (perm: string) => boolean;
  showCancelDialog: boolean;
  setShowCancelDialog: (open: boolean) => void;
  cancelMutation: { isPending: boolean; mutate: () => void };
  organizationId?: string;
};

export default function SubscriptionTab(props: SubscriptionTabProps) {
  const { t, i18n } = useTranslation(['pages.settings']);
  const {
    subscriptionPlan,
    subscriptionUsage: subscriptionLimits, // Rename to match the data structure
    subscription,
    invoices,
    availablePlans,
    selectedProUsers,
    setSelectedProUsers,
    selectedRiseUsers,
    setSelectedRiseUsers,
    billingPeriods,
    setBillingPeriods,
    calculateProPrice,
    calculateRisePrice,
    formatPrice,
    isDowngrade,
    formatDate,
    getStatusBadge,
    upgradeMutation,
    setUpgradeDialog,
    hasPermission,
    showCancelDialog,
    setShowCancelDialog,
    cancelMutation,
  } = props;

  const { formatDate: formatUserDate } = useDateFormat();
  const [showSeatManager, setShowSeatManager] = useState(false);
  const organizationId = props.organizationId;
  const formatEuroAmount = (amount: number) =>
    new Intl.NumberFormat(i18n.language || undefined, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  const isUnlimitedLimit = (value: unknown) => {
    if (value === null || value === undefined) return true;
    const numeric = Number(value);
    return !Number.isFinite(numeric) || numeric < 0 || numeric >= 999999;
  };
  const resolveLimitValue = (...values: unknown[]): number | null => {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
    }
    return null;
  };
  const currentPeriodStart = subscription?.current_period_start || subscription?.currentPeriodStart;
  const currentPeriodEnd = subscription?.current_period_end || subscription?.currentPeriodEnd;
  const subscriptionProvider = subscription?.provider || subscription?.providerName || null;
  const isStoreManaged = subscriptionProvider === 'apple' || subscriptionProvider === 'google';
  const isExpired = ['expired', 'cancelled', 'refunded'].includes(subscription?.status);
  const seatCount = subscription?.seat_count || subscription?.seatCount || subscriptionLimits?.maxTeamMembers || 0;
  const activePlanSlug = subscriptionPlanNameToSlug(
    subscription?.plan_name || subscriptionPlan?.plan_name
  );
  const currentPlanName = isExpired
    ? t('pages.settings:sections.subscription.plans.free')
    : t(`pages.settings:sections.subscription.plans.${activePlanSlug}`, {
        defaultValue: subscription?.plan_name || subscriptionPlan?.plan_name || '',
      });
  const currentPlan = availablePlans.find((p: any) =>
    p.id === subscription?.subscription_plan_id ||
    p.plan_name === (subscription?.plan_name || subscriptionPlan?.plan_name) ||
    p.id === subscriptionPlan?.subscription_plan_id
  );
  const billingInterval: 'monthly' | 'quarterly' | 'yearly' = (() => {
    const persistedInterval = subscription?.billing_interval || subscription?.billingInterval;
    if (persistedInterval === 'monthly' || persistedInterval === 'quarterly' || persistedInterval === 'yearly') {
      return persistedInterval;
    }
    if (currentPeriodEnd && currentPeriodStart) {
      const periodDays =
        (new Date(currentPeriodEnd).getTime() - new Date(currentPeriodStart).getTime()) /
        (1000 * 60 * 60 * 24);
      if (periodDays > 300) return 'yearly';
      if (periodDays > 60) return 'quarterly';
      return 'monthly';
    }
    return 'monthly';
  })();
  const intervalText = billingInterval === 'yearly'
    ? t('pages.settings:sections.subscription.billing.perYear')
    : billingInterval === 'quarterly'
      ? t('pages.settings:sections.subscription.billing.perQuarter')
      : t('pages.settings:sections.subscription.billing.perMonth');
  const unitPrice = billingInterval === 'yearly'
    ? (currentPlan?.price_per_seat_yearly || subscriptionPlan?.price_per_seat_yearly || subscription?.price_per_seat_yearly)
    : (currentPlan?.price_per_seat_monthly || subscriptionPlan?.price_per_seat_monthly || subscription?.price_per_seat_monthly);
  const statusValue = subscriptionLimits?.status || subscription?.status;
  const dateMeta = (() => {
    if (subscription?.status === 'active' && currentPeriodEnd) {
      return {
        label: subscription?.cancel_at_period_end || subscription?.cancelAtPeriodEnd
          ? t('pages.settings:sections.subscription.expiresOn')
          : t('pages.settings:sections.subscription.renewsOn'),
        value: formatUserDate(currentPeriodEnd),
      };
    }
    if ((subscription?.status === 'expired' || subscription?.status === 'cancelled') && currentPeriodEnd) {
      return {
        label: t('pages.settings:sections.subscription.expiredOn'),
        value: formatUserDate(currentPeriodEnd),
      };
    }
    if (currentPeriodEnd) {
      return {
        label: t('pages.settings:sections.subscription.expiresOn'),
        value: formatUserDate(currentPeriodEnd),
      };
    }
    return null;
  })();
  const isFreePlan = (plan: SubPlan): boolean => {
    const freeAliases = new Set(['free', 'kostenlos', 'ucretsiz', 'ücretsiz']);
    const normalizedId = (plan.id || '').toLowerCase().trim();
    const name = plan.planName ?? plan.plan_name ?? '';
    const normalizedName = name.toLowerCase().trim();
    return freeAliases.has(normalizedId) || freeAliases.has(normalizedName);
  };
  const sortedAvailablePlans = [...availablePlans].sort((a, b) => {
    const aIsFree = isFreePlan(a);
    const bIsFree = isFreePlan(b);
    if (aIsFree && !bIsFree) return -1;
    if (!aIsFree && bIsFree) return 1;
    return 0;
  });

  return (
    <div>
        {/* Subscription Info Banner - Only visible if subscription and limits are available */}
        {subscription && subscriptionLimits && (
          <div className="bg-white border-b border-light px-4 py-3 mb-6 rounded-lg shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
              <div className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-500" />
                <span className="text-sm font-semibold text-slate-900">{currentPlanName}</span>
                {subscriptionProvider && (
                  <Badge variant="outline" className="capitalize">
                    {t(`pages.settings:sections.subscription.providers.${subscriptionProvider}`, {
                      defaultValue: subscriptionProvider,
                    })}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium text-slate-700">{t('pages.settings:sections.subscription.status')}:</span>
                  <div className="flex items-center">
                    {statusValue ? getStatusBadge(statusValue) : <Badge variant="outline">{t('pages.settings:sections.subscription.noData')}</Badge>}
                  </div>
                </div>
                {dateMeta && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>{dateMeta.label}: {dateMeta.value}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-3 text-sm">
              {/* Seats Purchased with Manage Button */}
              <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
                <Users className="h-4 w-4 text-slate-500" />
                <div className="flex-1">
                  <div className="text-xs text-slate-500">{t('pages.settings:sections.subscription.usage.seatsPurchased')}</div>
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-slate-900">{seatCount}</div>
                    {!isExpired && !isStoreManaged && hasPermission('manage_organization') && organizationId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowSeatManager(true)}
                        className="h-6 px-2 text-xs text-[#4A90E2] hover:text-[#3a7bc8]"
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        {t('pages.settings:sections.subscription.manageSeats')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              {/* Seats Used */}
              <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
                {subscriptionLimits.currentTeamMembers > seatCount ? (
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                ) : (
                  <Users className="h-4 w-4 text-slate-500" />
                )}
                <div>
                  <div className="text-xs text-slate-500">{t('pages.settings:sections.subscription.usage.seatsUsed')}</div>
                  <div className={`font-medium ${subscriptionLimits.currentTeamMembers > seatCount ? 'text-amber-600' : 'text-slate-900'}`}>
                    {subscriptionLimits.currentTeamMembers}/{seatCount}
                    {subscriptionLimits.currentTeamMembers > seatCount && (
                      <span className="text-xs ml-1">⚠️</span>
                    )}
                  </div>
                </div>
              </div>
              {/* Projects */}
              <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
                <FolderOpen className="h-4 w-4 text-slate-500" />
                <div>
                  <div className="text-xs text-slate-500">{t('pages.settings:sections.subscription.usage.projects')}</div>
                  <div className="font-medium text-slate-900">
                    {subscriptionLimits.currentProjects}/
                    {isUnlimitedLimit(subscriptionLimits.maxProjects)
                      ? t('pages.settings:sections.subscription.features.projectsUnlimitedShort')
                      : subscriptionLimits.maxProjects}
                  </div>
                </div>
              </div>
              {/* PDF Plans */}
              <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
                <FileText className="h-4 w-4 text-slate-500" />
                <div>
                  <div className="text-xs text-slate-500">{t('pages.settings:sections.subscription.usage.pdfPlans')}</div>
                  <div className="font-medium text-slate-900">
                    {subscriptionLimits.currentPdfPlans}/
                    {isUnlimitedLimit(subscriptionLimits.maxPdfPlans)
                      ? t('pages.settings:sections.subscription.features.pdfPlansUnlimited')
                      : subscriptionLimits.maxPdfPlans}
                  </div>
                  {!isUnlimitedLimit(subscriptionLimits.maxPdfPlans) ? (
                    <div className="text-[11px] text-slate-500 leading-snug mt-0.5 max-w-[14rem]">
                      {t('pages.settings:sections.subscription.usage.pdfPlansOrgScopeNote')}
                    </div>
                  ) : null}
                </div>
              </div>
              {/* Active Tickets */}
              <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
                <AlertCircle className="h-4 w-4 text-slate-500" />
                <div>
                  <div className="text-xs text-slate-500">{t('pages.settings:sections.subscription.usage.activeTickets')}</div>
                  <div className="font-medium text-slate-900">
                    {subscriptionLimits.currentActiveTickets}/
                    {isUnlimitedLimit(subscriptionLimits.maxActiveTickets)
                      ? t('pages.settings:sections.subscription.features.projectsUnlimitedShort')
                      : subscriptionLimits.maxActiveTickets}
                  </div>
                </div>
              </div>
            </div>
            {/* Total Cost - single line below */}
            <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 mt-3 text-sm">
              <span className="text-xs text-slate-500">{t('pages.settings:sections.subscription.totalCost')}:</span>
              <span className="font-medium text-slate-900">
                {isStoreManaged ? (
                  t('pages.settings:sections.subscription.storeManagedTotal')
                ) : isExpired || !unitPrice ? (
                  t('pages.settings:sections.subscription.plans.free')
                ) : (
                  t('pages.settings:sections.subscription.totalCostFormula', {
                    seats: seatCount,
                    pricePerSeat: formatEuroAmount(unitPrice / 100),
                    total: formatEuroAmount((unitPrice / 100) * seatCount),
                    interval: intervalText
                  })
                )}
              </span>
            </div>
            {isStoreManaged && (
              <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
                {t('pages.settings:sections.subscription.manageSubscriptionInStore')}
              </div>
            )}
          </div>
        )}


        {/* Available subscriptions */}
        {hasPermission('manage_organization') && (
          <div className="mb-8">
            <h2 className="text-xs font-medium text-slate-700 mb-4">{t('pages.settings:sections.subscription.labels.availableSubscriptions')}</h2>
            {isStoreManaged && (
              <div className="mb-4 rounded-md bg-slate-50 px-4 py-3 text-sm text-slate-700">
                {t('pages.settings:sections.subscription.switchToWebAfterStore')}
              </div>
            )}
            <div className="flex flex-wrap gap-4 items-stretch">
              {sortedAvailablePlans.map((plan: any) => {
                const isPopular =
                  plan.displayFeatures?.some((f: DisplayFeature) => f.key === 'popular') ||
                  plan.features?.includes('popular');
                const planName = plan.planName ?? plan.plan_name;
                const priceMonthly = plan.pricePerSeatMonthly ?? plan.price_per_seat_monthly;
                const priceYearly = plan.pricePerSeatYearly ?? plan.price_per_seat_yearly;
                const displayFeatures = plan.displayFeatures ?? [];
                const systemKeys = ['project_count', 'seat_count', 'pdf_plan_count'];
                const customFeatures = displayFeatures.filter(
                  (f: DisplayFeature) => !systemKeys.includes(f.key)
                );
                const maxProjectsPerSeat = resolveLimitValue(
                  plan.maxProjectsPerSeat,
                  plan.max_projects_per_seat,
                  plan.maxProjects,
                  plan.max_projects
                );
                const maxPdfPlansPerSeat = resolveLimitValue(
                  plan.maxPdfPlansPerSeat,
                  plan.max_pdf_plans_per_seat,
                  plan.maxPdfPlans,
                  plan.max_pdf_plans
                );
                return (
                <Card key={plan.id} className="relative flex flex-col flex-1 min-w-[200px]">
                  {isPopular && (
                    <div className="absolute -top-3 left-4 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                      {t('pages.settings:sections.subscription.popular')}
                    </div>
                  )}
                  <CardContent className="p-4 flex flex-col flex-1">
                    <div className="text-center mb-4 min-h-[120px] flex flex-col justify-start">
                      <h3 className="text-lg font-medium text-slate-900 mb-2">{planName}</h3>
                        <div className="text-2xl font-bold text-slate-900">
                          {(() => {
                            const isYearly = billingPeriods[plan.id] === 'yearly';
                            const priceInCents = isYearly && priceYearly
                              ? priceYearly
                              : priceMonthly || 0;
                            if (priceInCents === 0) {
                              return t('pages.settings:sections.subscription.plans.free');
                            }
                            return `€${(priceInCents / 100).toFixed(0)}`;
                          })()}
                        </div>
                      <p className="text-xs text-slate-600 mb-3">
                        {(() => {
                          const isYearly = billingPeriods[plan.id] === 'yearly';
                          const interval = isYearly ? t('pages.settings:sections.subscription.billing.perYear') : t('pages.settings:sections.subscription.billing.perMonth');
                          return `per seat ${interval}`;
                        })()}
                      </p>
                      {(priceMonthly || priceYearly) ? (
                        <div className="flex w-full border border-gray-300">
                          <button
                            className={`flex-1 py-1 px-0 text-xs font-medium transition-colors w-full ${billingPeriods[plan.id] === 'monthly' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            onClick={() => setBillingPeriods(prev => ({ ...prev, [plan.id]: 'monthly' }))}
                          >
                            {t('pages.settings:sections.subscription.billing.monthly')}
                          </button>
                          <button
                            className={`flex-1 py-1 px-0 text-xs font-medium transition-colors border-l border-gray-300 w-full ${billingPeriods[plan.id] === 'yearly' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            onClick={() => setBillingPeriods(prev => ({ ...prev, [plan.id]: 'yearly' }))}
                          >
                            {t('pages.settings:sections.subscription.billing.yearly')}
                          </button>
                        </div>
                      ) : (
                        <div className="h-[28px] flex items-center"></div>
                      )}
                    </div>
                    <ul className="space-y-0 text-xs text-slate-600 mb-4 flex-1">
                      <li className="flex items-center py-2 border-b border-slate-200"><span className="mr-2">✓</span><span>{isUnlimitedLimit(maxProjectsPerSeat) ? t('pages.settings:sections.subscription.features.projectsUnlimitedShort') : `${maxProjectsPerSeat} ${t('pages.settings:sections.subscription.usage.projects')} / seat`}</span></li>
                      <li className="flex items-center py-2 border-b border-slate-200"><span className="mr-2">✓</span><span>{isUnlimitedLimit(maxPdfPlansPerSeat) ? t('pages.settings:sections.subscription.features.pdfPlansUnlimited') : `${maxPdfPlansPerSeat} ${t('pages.settings:sections.subscription.usage.pdfPlans')} / seat`}</span></li>
                      <li className="flex items-center py-2 border-b border-slate-200"><span className="mr-2">✓</span><span>{`${plan.min_seats || 1}+ ${t('pages.settings:sections.subscription.usage.teamMembers')}`}</span></li>
                      {customFeatures.map((f: DisplayFeature) => (
                        <li key={f.key} className="flex items-center py-2 border-b border-slate-200">
                          <span className="mr-2">✓</span><span>{f.label}</span>
                        </li>
                      ))}
                    </ul>
                    {(() => {
                      // Determine current plan (if expired/cancelled, treat as Free)
                      const isExpired = subscription?.status === 'expired' || subscription?.status === 'cancelled';
                      const currentPlanId = isExpired ? null : (subscription?.subscription_plan_id || subscription?.plan);
                      const currentPlanName = isExpired ? 'Free' : (subscription?.plan_name || subscriptionPlan?.plan_name || 'Free');
                      
                      // Plan hierarchy (higher number = better plan)
                      const planHierarchy: Record<string, number> = {
                        'kostenlos': 0,
                        'free': 0,
                        'expert': 1,
                        'pro': 2,
                        'rise': 3,
                        'enterprise': 4
                      };
                      
                      // Find current plan level by matching plan name or ID
                      let currentPlanLevel = 0;
                      if (!isExpired && currentPlanName) {
                        const planNameLower = currentPlanName.toLowerCase();
                        currentPlanLevel = planHierarchy[planNameLower] ?? 0;
                      }
                      
                      // Find this plan's level
                      const thisPlanId = plan.id?.toLowerCase();
                      const thisPlanName = planName?.toLowerCase();
                      const thisPlanLevel = planHierarchy[thisPlanId] ?? planHierarchy[thisPlanName] ?? 0;
                      
                      // Check if this is the current plan (match by ID or name)
                      const isCurrentPlan = !isExpired && (
                        (currentPlanId && (currentPlanId === plan.id || currentPlanId === plan.subscription_plan_id)) ||
                        (currentPlanName && currentPlanName.toLowerCase() === planName?.toLowerCase())
                      );
                      
                      // Check if this is a downgrade
                      const isThisDowngrade = thisPlanLevel < currentPlanLevel;
                      
                      // Check if current plan is highest (all lower plans should be disabled)
                      const highestPlanLevel = Math.max(...availablePlans.map((p: any) => {
                        const pId = p.id?.toLowerCase();
                        const pName = p.plan_name?.toLowerCase();
                        return planHierarchy[pId] ?? planHierarchy[pName] ?? 0;
                      }));
                      const isCurrentPlanHighest = currentPlanLevel >= highestPlanLevel;
                      
                      // Determine button text and disabled state
                      let buttonText = '';
                      let isDisabled = upgradeMutation.isPending || isStoreManaged;
                      
                      if (isCurrentPlan) {
                        // Current plan: Show "Subscribed" and disable
                        buttonText = t('pages.settings:sections.subscription.buttons.subscribed');
                        isDisabled = true;
                      } else if (currentPlanLevel === 0 || isExpired) {
                        // On Free plan: All plans show "Plan Seç"
                        buttonText = t('pages.settings:sections.subscription.buttons.selectPlan');
                        isDisabled = upgradeMutation.isPending || isStoreManaged || !(priceMonthly || priceYearly);
                      } else if (isThisDowngrade || (isCurrentPlanHighest && thisPlanLevel < currentPlanLevel)) {
                        // Lower plan (downgrade): Disable
                        buttonText = t('pages.settings:sections.subscription.buttons.selectPlan');
                        isDisabled = true;
                      } else {
                        // Higher plan: Show "Upgrade Plan"
                        buttonText = t('pages.settings:sections.subscription.buttons.upgradePlan');
                        isDisabled = upgradeMutation.isPending || isStoreManaged || !(priceMonthly || priceYearly);
                      }
                      
                      return (
                        <Button
                          className="bg-white hover:bg-white w-full"
                          variant="pillOutline"
                          disabled={isDisabled}
                          onClick={() => {
                            if ((priceMonthly || priceYearly) && !isDisabled) {
                              const billing = billingPeriods[plan.id] || 'monthly';
                              const featureLabels = customFeatures.map((f: DisplayFeature) => f.label);
                              const isPerSeat = true;
                              
                              let selectedPrice: number;
                              selectedPrice = billing === 'yearly' && priceYearly
                                ? priceYearly
                                : priceMonthly || 0;
                              
                              const price = `€${(selectedPrice / 100).toFixed(0)}`;
                              const planMinSeats = plan.min_seats || 1;
                              const maxSeats = plan.max_seats;
                              // Ensure minimum seats accommodates current team members
                              const currentUsedSeats = subscriptionLimits?.currentTeamMembers || 1;
                              const effectiveMinSeats = Math.max(planMinSeats, currentUsedSeats);
                              
                              props.setUpgradeDialog({
                                open: true,
                                planId: plan.id,
                                planName: planName ?? '',
                                price,
                                duration: billing === 'yearly' ? t('pages.settings:sections.subscription.billing.perYear') : t('pages.settings:sections.subscription.billing.perMonth'),
                                billing,
                                selectedUsers: effectiveMinSeats,
                                currentUsers: currentUsedSeats,
                                maxUsers: plan.max_seats || 999,
                                maxProjects: plan.max_projects_per_seat
                                  ? (plan.max_projects_per_seat * effectiveMinSeats)
                                  : 999,
                                features: featureLabels.length > 0 ? featureLabels : (plan.features || []),
                                isPerSeat,
                                pricePerSeat: selectedPrice,
                                minSeats: planMinSeats,
                                maxSeats,
                              });
                            }
                          }}
                        >
                          {buttonText}
                        </Button>
                      );
                    })()}
                  </CardContent>
                </Card>
              );
              })}
            </div>
          </div>
        )}

        {/* Invoices */}
        {hasPermission('manage_organization') && (
          <div className="mb-8">
            <h2 className="text-xs font-medium text-slate-700 mb-4">{t('pages.settings:sections.subscription.invoices.title')}</h2>
            <Card>
              <CardContent className="p-4">
                {Array.isArray(invoices) && invoices.length > 0 ? (
                  <div className="space-y-2">
                    {invoices.map((invoice: any) => (
                      <div key={invoice.id} className="flex items-center justify-between p-2 border border-slate-200 rounded bg-white">
                        <div>
                          <div className="text-xs font-medium text-slate-900">{invoice.number || t('pages.settings:sections.subscription.invoices.invoice')}</div>
                          <p className="text-xs text-slate-600">{formatDate(invoice.createdAt || invoice.created_at)}</p>
                          {invoice.amount && (
                            <p className="text-xs text-slate-500">
                              {new Intl.NumberFormat('de-DE', { 
                                style: 'currency', 
                                currency: invoice.currency || 'EUR' 
                              }).format((invoice.amount || 0) / 100)}
                            </p>
                          )}
                        </div>
                        {invoice.invoiceUrl ? (
                          <Button 
                            size="sm" 
                            variant="pillOutline" 
                            className="bg-white hover:bg-white"
                            onClick={() => window.open(invoice.invoiceUrl, '_blank')}
                          >
                            {t('pages.settings:sections.subscription.invoices.download')}
                          </Button>
                        ) : (
                          <Button size="sm" variant="pillOutline" className="bg-white hover:bg-white" disabled>
                            {t('pages.settings:sections.subscription.invoices.download')}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-600">{t('pages.settings:sections.subscription.invoices.noInvoices')}</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Cancel subscription */}
        {subscription && subscription.status === 'active' && !(subscription.cancel_at_period_end || subscription.cancelAtPeriodEnd) && !isStoreManaged && (
          <div className="mb-8">
            <h2 className="text-xs font-medium text-slate-700 mb-4">{t('pages.settings:sections.subscription.management.title')}</h2>
            <Card>
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xs font-medium text-slate-700">{t('pages.settings:sections.subscription.management.cancelTitle')}</h3>
                    <p className="text-sm text-slate-600">{t('pages.settings:sections.subscription.management.cancelDescription')}</p>
                  </div>
                  <Button variant="pillOutline" className="bg-white hover:bg-white" onClick={() => setShowCancelDialog(true)} disabled={cancelMutation.isPending}>
                    {t('pages.settings:sections.subscription.management.cancelButton')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Cancel confirmation dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-500" />
                {t('pages.settings:sections.subscription.cancelDialog.title')}
              </DialogTitle>
              <DialogDescription className="space-y-3 pt-2">
                <div className="text-slate-700"><strong>{t('pages.settings:sections.subscription.cancelDialog.importantInfo')}</strong></div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
                  {t('pages.settings:sections.subscription.cancelDialog.expiresMessage')}{' '}
                  <strong>
                    {subscription?.current_period_end || subscription?.currentPeriodEnd
                      ? formatUserDate(subscription.current_period_end || subscription.currentPeriodEnd)
                      : t('pages.settings:sections.subscription.cancelDialog.endOfBillingPeriod')
                    }
                  </strong>.
                </div>
                <div className="text-sm text-slate-600" dangerouslySetInnerHTML={{ __html: t('pages.settings:sections.subscription.cancelDialog.details') }}></div>
                <div className="text-sm font-medium text-slate-700">{t('pages.settings:sections.subscription.cancelDialog.confirmQuestion')}</div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="pillOutline" className="bg-white hover:bg-white flex-1" onClick={() => setShowCancelDialog(false)}>
                {t('pages.settings:sections.subscription.cancelDialog.keepSubscription')}
              </Button>
              <Button variant="pillOutline" className="bg-white hover:bg-white flex-1" onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending}>
                {cancelMutation.isPending ? t('pages.settings:sections.subscription.cancelDialog.canceling') : t('pages.settings:sections.subscription.cancelDialog.cancelNow')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Seat Management Dialog */}
        {organizationId && (
          <SeatUpgradePrompt
            orgId={organizationId}
            currentSeats={seatCount}
            maxTeamMembers={subscriptionLimits?.maxTeamMembers || seatCount}
            activeMembers={subscriptionLimits?.currentTeamMembers || 0}
            pricePerSeat={unitPrice ? unitPrice / 100 : 29}
            currency={currentPlan?.currency || 'EUR'}
            variant="dialog"
            showTrigger={false}
            open={showSeatManager}
            onOpenChange={setShowSeatManager}
          />
        )}
    </div>
  );
}
